import { create } from 'zustand'
import { ChatbotFeedback, ChatbotSource } from '../../services/chatbot'

export type ChatMessageRole = "user" | "assistant";

export type ChatMessage = {
    /** Identifiant client (stable pour la clé React et la mise à jour du feedback). */
    id: string;
    role: ChatMessageRole;
    content: string;
    /** messageId renvoyé par le backend — nécessaire pour poster un feedback. Absent pour les messages utilisateur. */
    serverId?: number;
    sources?: ChatbotSource[];
    feedback?: ChatbotFeedback;
    /** Empêche le spam de clics pendant l'appel réseau du feedback. */
    feedbackPending?: boolean;
    /** La question a échoué : bulle assistant affichée en mode "erreur", pas de feedback possible. */
    error?: boolean;
}

type ChatbotStore = {
    messages: ChatMessage[];
    conversationId: number | null;
    loading: boolean;
    /** Ouverture du tiroir. Dans le store et non dans le widget : plusieurs surfaces
     *  (bouton de la barre supérieure, bandeau de l'accueil) doivent pouvoir l'ouvrir. */
    open: boolean;
    /** Question posée depuis l'extérieur du tiroir (bandeau d'accueil). Le panneau la
     *  consomme à l'ouverture puis la remet à null. */
    pendingQuestion: string | null;
    addMessage: (message: ChatMessage) => void;
    updateMessage: (id: string, patch: Partial<ChatMessage>) => void;
    setLoading: (loading: boolean) => void;
    setConversationId: (conversationId: number) => void;
    setOpen: (open: boolean) => void;
    setPendingQuestion: (question: string | null) => void;
    reset: () => void;
}

// `open` est délibérément absent : reset() est déclenché par « Nouvelle conversation »
// depuis l'en-tête du tiroir, qui doit rester ouvert après le nettoyage.
const initialState = {
    messages: [] as ChatMessage[],
    conversationId: null as number | null,
    loading: false,
    pendingQuestion: null as string | null,
};

// Mémoire de session uniquement : pas de persist() ici, la conversation ne doit
// pas survivre à un rechargement de page.
export const useChatbotStore = create<ChatbotStore>((set) => ({
    ...initialState,
    open: false,
    addMessage: (message) => set((state) => ({ messages: [...state.messages, message] })),
    updateMessage: (id, patch) => set((state) => ({
        messages: state.messages.map((message) => (message.id === id ? { ...message, ...patch } : message)),
    })),
    setLoading: (loading) => set({ loading }),
    setConversationId: (conversationId) => set({ conversationId }),
    setOpen: (open) => set({ open }),
    setPendingQuestion: (pendingQuestion) => set({ pendingQuestion }),
    reset: () => set({ ...initialState, messages: [] }),
}))
